import { Children, cloneElement, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useApplicationsGetQuery } from '../../Common/Queries/applicationsQueries';
import { useLetterheadGetQuery } from '../../Common/Queries/letterheadQueries';
import { useLocationGetQuery } from '../../Common/Queries/locationQueries';
import {
  useApplicantStatsCountGetQuery,
  usePositionsGetQueryById,
  usePositionsKanbanGetQueryById,
  usePositionsPutQueryById,
  usePositionsPutStatusQueryById,
} from '../../Common/Queries/positionsQueries';
import { useStreamGetQuery } from '../../Common/Queries/streamQueries';
import { useTemplateGetQuery } from '../../Common/Queries/templateQueries';
import { useUsersGetQuery } from '../../Common/Queries/usersQueries';

import { isLogin } from '../../utils';

import { ALL_DATA_LIMIT, AuthContext, DURATION_TIME, ROLES } from '../../constants';

export const ViewPositionContainer = ({ children, positionID }) => {
  const queryClient = useQueryClient();
  const [filterValues, setFilterValues] = useState({ applicationStart: '', applicationEnd: '' });

  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles && userInfo?.roles[0];

  const putToPosition = usePositionsPutQueryById();
  const positionData = usePositionsGetQueryById(
    {
      reqId: positionID,
    },
    {}
  );
  const positionApplications = useApplicationsGetQuery(
    {
      positionID,
      'createdAtRange.fromDate': filterValues.applicationStart,
      'createdAtRange.toDate': filterValues.applicationEnd,
      'createdAtRange.includeDate': true,
      limit: 0,
    },
    { enabled: !!positionID }
  );
  const applicationBreakdownStats = useApplicantStatsCountGetQuery(
    {
      reqId: positionID,
      applicationStart: filterValues.applicationStart,
      applicationEnd: filterValues.applicationEnd,
    },
    { enabled: !!positionID }
  );
  const applicationStats = useMemo(() => {
    const stats = (positionApplications.data?.data || []).reduce((acc, application) => {
      const status = application.updatedStatus || application.status;

      if (!status) return acc;
      return { ...acc, [status]: (acc[status] || 0) + 1 };
    }, {});

    return { ...positionApplications, data: stats };
  }, [positionApplications]);
  const positionByKanban = usePositionsKanbanGetQueryById({ reqId: positionID });
  const putPosition = usePositionsPutStatusQueryById();

  const streamList = useStreamGetQuery(
    { limit: ALL_DATA_LIMIT },
    { enabled: isLogin(), refetchInterval: DURATION_TIME, refetchIntervalInBackground: true }
  );

  const usersList = useUsersGetQuery(
    {
      limit: ALL_DATA_LIMIT,
      role: [
        ROLES.ADMIN,
        ROLES.HR,
        ROLES.HIRING_MANAGER,
        ROLES.SUPER_ADMIN,
        ROLES.TENANT_OWNER,
      ].join(','),
    },
    {
      enabled: isLogin() && role !== ROLES.AGENCY,
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );
  const offerLetterTemplatesList = useTemplateGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled:
        isLogin() && (role === ROLES.HR || role === ROLES.ADMIN, role === ROLES.TENANT_OWNER),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const letterheadData = useLetterheadGetQuery({ enabled: isLogin() });

  /**
   * this function is handler function for trigger put-status query
   * @function handlePutStatus
   */
  const handlePutStatus = (status) => {
    const reqParams = { reqBody: { ...positionData.data, status }, reqId: positionID };
    putPosition.mutate(reqParams);
  };

  const locations = useLocationGetQuery({ limit: 0 });

  const handleFilterValues = (nextFilterValues) => {
    setFilterValues((prev) => {
      const resolvedFilterValues =
        typeof nextFilterValues === 'function' ? nextFilterValues(prev) : nextFilterValues;

      return {
        applicationStart: resolvedFilterValues?.applicationStart || '',
        applicationEnd: resolvedFilterValues?.applicationEnd || '',
      };
    });
    queryClient.invalidateQueries(['applicantStatsGetQuery']);
    queryClient.invalidateQueries(['applicationsGetQuery']);
  };

  const child = Children.only(children);
  return cloneElement(child, {
    positionID,
    positionData,
    putPosition,
    filterValues,
    setFilterValues: handleFilterValues,
    applicationStats,
    applicationBreakdownStats,
    handlePutStatus,
    putToPosition,
    locations,
    positionByKanban,
    offerLetterTemplatesList,
    usersList,
    streamList,
    letterheadData,
  });
};
